import { useCallback, useReducer } from 'react';
import {
  IGetAdsRequestFilter,
  IGetAdsRequestRange,
  ISearchPageFilter,
  ISearchPageFilterValue,
  TypePrice,
} from '../../../api/types/searchPageApiTypes';
import { CURRENCIES } from 'types';
import { useLocationContext } from 'features/location/Location.context';

enum ACTION_TYPE {
  ADD_MULTI_SELECT_VALUE = 'ADD_MULTI_SELECT_VALUE',
  REMOVE_MULTI_SELECT_VALUE = 'REMOVE_MULTI_SELECT_VALUE',
  SET_RANGE = 'SET_RANGE',
  SET_RANGES = 'SET_RANGES',
  DELETE_RANGE = 'DELETE_RANGE',
  SET_FILTER = 'SET_FILTER',
  SET_FILTERS = 'SET_FILTERS',
  DELETE_FILTER = 'DELETE_FILTER',
  RESET = 'RESET',
  SET_FILTER_AND_RANGE = 'SET_FILTER_AND_RANGE',
  SET_FILTER_AND_RANGE_VALUES = 'SET_FILTER_AND_RANGE_VALUES',
}

export type SelectOptionType = {
  label: string;
  value: string;
};

export type FilterType = IGetAdsRequestFilter;
export type RangeType = IGetAdsRequestRange;

type AddFilterValueType = {
  type: ACTION_TYPE.ADD_MULTI_SELECT_VALUE;
  payload: FilterType;
};
type RemoveFilterValueType = {
  type: ACTION_TYPE.REMOVE_MULTI_SELECT_VALUE;
  payload: {
    name: string;
    value: string;
  };
};
type SetFilterType = { type: ACTION_TYPE.SET_FILTER; payload: FilterType };
type SetFiltersType = { type: ACTION_TYPE.SET_FILTERS; payload: FilterType[] };
type DeleteFilterType = { type: ACTION_TYPE.DELETE_FILTER; payload: string };
type SetRangeType = { type: ACTION_TYPE.SET_RANGE; payload: RangeType };
type DeleteRangeType = { type: ACTION_TYPE.DELETE_RANGE; payload: string };
type ResetType = { type: ACTION_TYPE.RESET };
type SetFilterAndRangeType = {
  type: ACTION_TYPE.SET_FILTER_AND_RANGE;
  payload: { filter: FilterType; range: RangeType };
};
type setFiltersAndRangesType = {
  type: ACTION_TYPE.SET_FILTER_AND_RANGE_VALUES;
  payload: {
    filterData: ISearchPageFilter[];
    filtersQueryParams?: FilterType[];
    rangeQueryParams?: IGetAdsRequestRange[];
  };
};

export type FiltersActionsType =
  | AddFilterValueType
  | RemoveFilterValueType
  | SetFilterType
  | SetFiltersType
  | SetRangeType
  | DeleteRangeType
  | DeleteFilterType
  | ResetType
  | SetFilterAndRangeType
  | setFiltersAndRangesType;

const getFilterByName = (filters: FilterType[], name: string) =>
  filters.find((filter) => filter.name === name);

const getRangeByName = (filters: RangeType[], name: string) =>
  filters.find((filter) => filter.name === name);

const getFilterWithoutName = (filters: FilterType[], name: string) =>
  filters.filter((filter) => filter.name !== name);

const getFilterByGroup = (filtersData: ISearchPageFilter[], group: string) =>
  filtersData.filter(
    (filterData) =>
      filterData.searchQueryGroup === group &&
      filterData.filterType.name !== 'Group',
  );

const mapRangeValue = (
  from?: string,
  to?: string,
  currency?: CURRENCIES,
  typePrice?: TypePrice,
) => ({
  ...(from && { from }),
  ...(to && { to }),
  ...(currency && { currency }),
  ...(typePrice && { typePrice }),
});

const mapFilters = (
  filtersData: ISearchPageFilter[],
  filtersQueryParams?: FilterType[],
): FilterType[] =>
  getFilterByGroup(filtersData, 'filters').map((filterData) => ({
    name: filterData.name,
    values:
      (filtersQueryParams &&
        getFilterByName(filtersQueryParams, filterData.name)?.values) ||
      [],
  }));

const mapRanges = (
  filtersData: ISearchPageFilter[],
  rangeQueryParams?: RangeType[],
): RangeType[] =>
  getFilterByGroup(filtersData, 'ranges').map((filterData) => {
    const rangeSelected =
      rangeQueryParams && getRangeByName(rangeQueryParams, filterData.name);
    return {
      name: filterData.name,
      ...mapRangeValue(
        rangeSelected?.from,
        rangeSelected?.to,
        rangeSelected?.currency,
        rangeSelected?.typePrice,
      ),
    };
  });

function useFiltersReducer(state: FilterStateType, action: FiltersActionsType) {
  switch (action.type) {
    case ACTION_TYPE.ADD_MULTI_SELECT_VALUE: {
      const selectedFilter = getFilterByName(
        state.filters,
        action.payload.name,
      );
      return selectedFilter
        ? {
            ...state,
            filters: [
              ...getFilterWithoutName(state.filters, selectedFilter.name),
              {
                ...selectedFilter,
                values: selectedFilter.values.concat(action.payload.values),
              },
            ],
          }
        : state;
    }
    case ACTION_TYPE.REMOVE_MULTI_SELECT_VALUE: {
      const selectedFilter = getFilterByName(
        state.filters,
        action.payload.name,
      );
      return selectedFilter
        ? {
            ...state,
            filters: [
              ...getFilterWithoutName(state.filters, selectedFilter.name),
              {
                ...selectedFilter,
                values: selectedFilter.values.filter(
                  (value) => value !== action.payload.value,
                ),
              },
            ],
          }
        : state;
    }
    case ACTION_TYPE.SET_FILTER: {
      const selectedFilter = getFilterByName(
        state.filters,
        action.payload.name,
      );
      return selectedFilter
        ? {
            ...state,
            filters: [
              ...getFilterWithoutName(state.filters, selectedFilter.name),
              {
                ...selectedFilter,
                values: action.payload.values,
              },
            ],
          }
        : state;
    }
    case ACTION_TYPE.SET_FILTERS:
      return { ...state, filters: action.payload };
    case ACTION_TYPE.DELETE_FILTER:
      return {
        ...state,
        filters: getFilterWithoutName(state.filters, action.payload),
      };
    case ACTION_TYPE.SET_RANGE: {
      const newRanges = [...state.ranges];
      const range = newRanges.find(
        (range) => range.name === action.payload.name,
      );
      if (range) {
        if (action.payload.from) {
          range.from = action.payload.from;
        } else if (range.from) {
          delete range.from;
        }
        if (action.payload.to) {
          range.to = action.payload.to;
        } else if (range.to) {
          delete range.to;
        }
        if (action.payload.currency) {
          range.currency = action.payload.currency;
        } else if (range.currency) {
          delete range.currency;
        }
        if (action.payload.typePrice) {
          range.typePrice = action.payload.typePrice;
        } else if (range.typePrice) {
          delete range.typePrice;
        }
      } else {
        newRanges.push({
          name: action.payload.name,
          ...(action.payload.from && { from: action.payload.from }),
          ...(action.payload.to && { to: action.payload.to }),
          ...(action.payload.currency && { currency: action.payload.currency }),
          ...(action.payload.typePrice && {
            typePrice: action.payload.typePrice,
          }),
        });
      }
      return { ...state, ranges: newRanges };
    }

    case ACTION_TYPE.SET_FILTER_AND_RANGE: {
      /**
       * This is a copy of the logic for ACTION_TYPE.SET_FILTER and ACTION_TYPE.SET_RANGE
       * I'll look at making this more dry in the future if this feature is rolled out to more than just the tractors section
       */

      const selectedFilter = getFilterByName(
        state.filters,
        action.payload.filter.name,
      );
      const filters = selectedFilter
        ? {
            ...state,
            filters: [
              ...getFilterWithoutName(state.filters, selectedFilter.name),
              {
                ...selectedFilter,
                values: action.payload.filter.values,
              },
            ],
          }
        : state;

      // Ranges
      const newRanges = [...state.ranges];
      const range = newRanges.find(
        (range) => range.name === action.payload.range.name,
      );
      if (range) {
        if (action.payload.range.from) {
          range.from = action.payload.range.from;
        } else if (range.from) {
          delete range.from;
        }
        if (action.payload.range.to) {
          range.to = action.payload.range.to;
        } else if (range.to) {
          delete range.to;
        }
        if (action.payload.range.currency) {
          range.currency = action.payload.range.currency;
        } else if (range.currency) {
          delete range.currency;
        }
        if (action.payload.range.typePrice) {
          range.typePrice = action.payload.range.typePrice;
        } else if (range.typePrice) {
          delete range.typePrice;
        }
      } else {
        newRanges.push({
          name: action.payload.range.name,
          ...(action.payload.range.from && { from: action.payload.range.from }),
          ...(action.payload.range.to && { to: action.payload.range.to }),
          ...(action.payload.range.currency && {
            currency: action.payload.range.currency,
          }),
          ...(action.payload.range.typePrice && {
            typePrice: action.payload.range.typePrice,
          }),
        });
      }

      return { ...state, ...filters, ranges: newRanges };
    }
    case ACTION_TYPE.DELETE_RANGE: {
      return {
        ...state,
        ranges: state.ranges.filter((item) => item.name !== action.payload),
      };
    }
    case ACTION_TYPE.RESET:
      return {
        ...state,
        filters: state.filters.map((filter) => ({
          name: filter.name,
          values: [],
        })),
        ranges: [],
      };
    case ACTION_TYPE.SET_FILTER_AND_RANGE_VALUES:
      const { filterData, filtersQueryParams, rangeQueryParams } =
        action.payload;
      const filters = mapFilters(filterData, filtersQueryParams);
      const ranges = mapRanges(filterData, rangeQueryParams);
      return {
        ...state,
        filters,
        ranges,
      };
    default:
      return state;
  }
}

export interface FilterStateType {
  filters: FilterType[];
  ranges: RangeType[];
}

export const defaultState: FilterStateType = {
  filters: [],
  ranges: [],
};

export const useFilters = (
  filtersData: ISearchPageFilter[],
  filtersQueryParams?: FilterType[],
  rangeQueryParams?: RangeType[],
) => {
  const [state, dispatch] = useReducer(useFiltersReducer, {
    filters: mapFilters(filtersData, filtersQueryParams),
    ranges: mapRanges(filtersData, rangeQueryParams),
  });

  const getRangeValues = (name: string) => getRangeByName(state.ranges, name);

  const { selectedCounty } = useLocationContext();

  const addMultiSelectValue = (filter: FilterType) => {
    return dispatch({
      type: ACTION_TYPE.ADD_MULTI_SELECT_VALUE,
      payload: filter,
    });
  };

  const removeMultiSelectValue = (name: string, value: string) => {
    return dispatch({
      type: ACTION_TYPE.REMOVE_MULTI_SELECT_VALUE,
      payload: {
        name,
        value,
      },
    });
  };

  const setFilter = (filter: FilterType) =>
    dispatch({
      type: ACTION_TYPE.SET_FILTER,
      payload: filter,
    });

  const setFilters = (filters: FilterType[]) =>
    dispatch({
      type: ACTION_TYPE.SET_FILTERS,
      payload: filters,
    });

  const deleteFilter = (name: string) =>
    dispatch({
      type: ACTION_TYPE.DELETE_FILTER,
      payload: name,
    });

  const setRange = (range: RangeType) =>
    dispatch({
      type: ACTION_TYPE.SET_RANGE,
      payload: range,
    });

  const setFilterAndRange = (filter: FilterType, range: RangeType) => {
    dispatch({
      type: ACTION_TYPE.SET_FILTER_AND_RANGE,
      payload: { filter, range },
    });
  };

  const deleteRange = (name: string) => {
    dispatch({
      type: ACTION_TYPE.DELETE_RANGE,
      payload: name,
    });
  };

  const resetFilters = () =>
    dispatch({
      type: ACTION_TYPE.RESET,
    });

  const setFilterAndRangeValues = (
    filterData: ISearchPageFilter[],
    filtersQueryParams?: FilterType[],
    rangeQueryParams?: IGetAdsRequestRange[],
  ) => {
    dispatch({
      type: ACTION_TYPE.SET_FILTER_AND_RANGE_VALUES,
      payload: { filterData, filtersQueryParams, rangeQueryParams },
    });
  };

  function calculateFiltersRangeCount(): number {
    let count = 0;
    for (const filter of state.filters) {
      if (filter.values.length > 1) {
        count += 1;
      } else if (filter.values.length === 1 && filter.values[0] !== '') {
        count += 1;
      }
    }
    for (const range of state.ranges) {
      if (
        typeof range.to !== 'undefined' ||
        typeof range.from !== 'undefined'
      ) {
        count += 1;
      }
    }
    if (selectedCounty) {
      count += 1;
    }
    return count;
  }

  const getFilter = useCallback(
    (name: string) =>
      filtersData.find((filterItem) => filterItem.name === name),
    [filtersData],
  );

  const getFilters = useCallback(
    (name: string) => getFilter(name)?.values,
    // TODO: Clean up this callback's dependencies. We're disabling this lint error for now so we
    // can clean up the lint logs. Ideally we should rewrite this code to be less error prone and
    // trust the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtersData],
  );

  const getFilterValue = useCallback(
    (filter: ISearchPageFilter) =>
      state.filters
        ?.find((item) => item.name === filter.name)
        ?.values.join('') ||
      ((filter.values && filter.values[0]?.value) ?? ''),
    [state.filters],
  );
  const getFilterValues = useCallback(
    (name: string) =>
      state.filters.find((filterItem) => filterItem.name === name)?.values,
    [state.filters],
  );

  const getFilterMultiValue = useCallback(
    (filter: ISearchPageFilter) => {
      const filterFromUrl = getFilterValues(filter.name);
      const filterSelected =
        filter.values &&
        filter.values.filter((filterValue) =>
          filterValue.value
            ? filterFromUrl?.includes(filterValue.value)
            : false,
        );
      return filterSelected && filterSelected.length > 0
        ? filterSelected
        : filter.values
        ? [filter.values[0]]
        : [];
    },
    // TODO: Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.filters],
  );

  return {
    state: {
      filters: state.filters,
      ranges: state.ranges,
      appliedFiltersRangeCount: calculateFiltersRangeCount(),
    },
    dispatch: {
      getFilter,
      getFilters,
      getFilterValue,
      getFilterMultiValue,
      getFilterValues,
      getRangeValues,
      addMultiSelectValue,
      removeMultiSelectValue,
      setFilter,
      setFilters,
      deleteFilter,
      setRange,
      setFilterAndRange,
      deleteRange,
      resetFilters,
      getFilterByName,
      getRangeByName,
      setFilterAndRangeValues,
    },
  };
};

export interface IFilterState extends FilterStateType {}
export interface IFilterActions {
  getFilter: (name: string) => ISearchPageFilter | undefined;
  getFilters: (name: string) => ISearchPageFilterValue[] | null | undefined;
  getFilterMultiValue: (filter: ISearchPageFilter) => ISearchPageFilterValue[];
  getFilterValue: (filter: ISearchPageFilter) => string;
  getFilterValues: (name: string) => string[] | undefined;
  getRangeValues: (name: string) => void;
  addMultiSelectValue: (filter: FilterType) => void;
  removeMultiSelectValue: (name: string, value: string) => void;
  setFilter: (filter: FilterType) => void;
  setFilters: (filters: FilterType[]) => void;
  deleteFilter: (name: string) => void;
  setRange: (range: RangeType) => void;
  setFilterAndRange: (filter: FilterType, range: RangeType) => void;
  getFilterByName: (
    filters: FilterType[],
    name: string,
  ) => IGetAdsRequestFilter | undefined;
  getRangeByName: (
    filters: RangeType[],
    name: string,
  ) => IGetAdsRequestRange | undefined;
  deleteRange: (name: string) => void;
  resetFilters: () => void;
  setFilterAndRangeValues: (
    filterData: ISearchPageFilter[],
    filtersQueryParams: FilterType[],
  ) => void;
}
